/**
 * townScene.ts — the town's authored surface and its monumental art, in ONE
 * implementation. The hub scene (`hub.ts`) and the sign-in scene
 * (`authScene.ts`) both draw through here, so the square you sign in to is
 * literally the square you land in: same plane, same pools, same door, same
 * braziers. Before this, the login page carried a fifth hand-drawn copy of
 * the door in CSS gradients — the "one implementation of world art" rule in
 * RENDERING.md is exactly why it can't stay that way.
 *
 * Presentation-only: no sim, no socket, no decisions, nothing a run can read.
 * The plane size stays in shared (`HUB_PLANE_W/H`) because the server bounds
 * the positions it relays with it — drawn plane and validated plane must be
 * one number.
 *
 * The door's "THE DUNGEON" crown inscription is GONE. It was an artifact of
 * the placeholder pass, and the painter pass left it nowhere else to live:
 * anything the door draws south of its own row is overpainted by the tiles
 * drawn after it, and at the crown it collided with the hub's title bar.
 */
import { HUB_PLANE_H, HUB_PLANE_W, TERRAIN_FLOOR, TERRAIN_POND, TERRAIN_TABLE } from "@pd/shared";
import type { Vec2 } from "./actions.js";
import { HALF_H, TILE_W } from "./iso.js";

export const TOWN_PLANE_W = HUB_PLANE_W;
export const TOWN_PLANE_H = HUB_PLANE_H;

/** The town's floor look — civic stone, kept LOW: this is a maintained
 *  public square, not another generated room. The hub hands these to the
 *  Viewport; the sign-in scene uses them directly, so the two floors wear
 *  identically. */
export const TOWN_FLOOR_PALETTE = "stone";
export const TOWN_FLOOR_ARCHITECTURE = "civic-stone-v1";
export const TOWN_FLOOR_WEAR = 8;

/** The dungeon door stands on the anti-diagonal row x+y = DOOR_ROW, DOOR_W
 *  tiles across, centred on x−y = 0 — which is the plane's horizontal centre
 *  on screen, so the door sits dead centre at the north end. (DOOR_ROW and
 *  DOOR_W−1 must share parity for the row to land on whole tiles.)
 *
 *  It reads as the north EDGE of the square, not a billboard standing in it:
 *  everything north of its row within its width is the door — clicking there
 *  is clicking the door, and you cannot walk into it. Walking around its ends
 *  is open (a strip of plane continues behind it), and the depth sort decides
 *  who is occluded, because the door is a real painter-pass prop — which also
 *  means the door may draw nothing south of its own row: those tiles are
 *  painted after it (`traps/coordinate-space.md`).
 *
 *  DOOR_ROW is far enough south that the camera has room above the arch: the
 *  camera centres on the hero and clamps at the plane's north edge, so a door
 *  taller than ~½ a screen standing at the plane's corner can never be seen
 *  whole — the head is cut off by the top of the window. Row 13 plus a door
 *  under 0.75·W tall fits on a 713 px viewport from anywhere in the crowd
 *  band below it (and the whole plane is one constant if it needs to grow). */
export const DOOR_ROW = 13;
export const DOOR_W = 4;

/** The door's height as a fraction of its width (both in px): 4 tiles across
 *  × 0.72 ≈ 276 px ≈ 4 hero heights, so you are dwarfed standing in front of
 *  it — the whole point of THE dungeon door — while its arch head still fits
 *  above the camera at any standing distance (see DOOR_ROW). */
export const DOOR_TALLNESS = 0.72;

/** The tiles the door stands on, west→east on screen. */
export const DOOR_TILES: Vec2[] = Array.from({ length: DOOR_W }, (_, i) => ({
  x: (DOOR_ROW - DOOR_W + 1) / 2 + i,
  y: (DOOR_ROW + DOOR_W - 1) / 2 - i,
}));

/** The door's draw anchor + painter depth: the midpoint of its row, which is
 *  also its centre on screen (x−y = 0 there). Fractional on purpose — a prop
 *  wider than one tile anchors between tiles. */
export const DOOR_ANCHOR: Vec2 = {
  x: (DOOR_TILES[0]!.x + DOOR_TILES[DOOR_W - 1]!.x) / 2,
  y: (DOOR_TILES[0]!.y + DOOR_TILES[DOOR_W - 1]!.y) / 2,
};

/** Two warm points frame the route without turning the plaza into a prop
 *  field. Their row is immediately south of the threshold, so the painter
 *  pass handles heroes crossing in front of them correctly. */
export const BRAZIER_TILES: readonly Vec2[] = [
  { x: 5, y: 11 },
  { x: 11, y: 5 },
];

/** A small civic seal gives the otherwise endless grid a visual centre and
 *  makes the crowd band read as a plaza rather than a test plane. */
export const SEAL_TILE: Vec2 = { x: 9, y: 9 };

/** SPAWN slots: where a presence stands until their first position report
 *  (allocated by the hub, never by id arithmetic), laid out in a diagonal
 *  BAND (constant x+y rows, 2-tile steps) just south of the door. The iso
 *  screen's "north" is the x+y diagonal, so a band keeps the crowd shallow in
 *  the depth that decides screen height: everyone stays roughly the same size
 *  on screen, the door stays clear of the whole band, and the crowd is the
 *  first thing you see. The sign-in scene stands its preview hero in the same
 *  band, which is why the band lives here rather than in the hub. */
export const CROWD_SLOTS: readonly Vec2[] = (() => {
  const out: Vec2[] = [];
  // Fill from the centre out. The old west→east order put the first solo
  // presence seven screen-columns left of the door, so the landmark was
  // cropped on the player's very first frame. A town arrival should compose
  // around its landmark; later arrivals then fan out symmetrically.
  // Four columns leave the side courts free for water and civic furniture;
  // eight shallow rows still hold the hub's full 32-presence capacity.
  const diffs = [-1, 1, -3, 3];
  for (let row = 0; row < 8; row++) {
    for (const d of diffs) {
      const sum = DOOR_ROW + 2 + row * 2; // x+y (odd): the first row south of the door
      const x = (sum + d) / 2; // x−y is odd — x,y are (sum±d)/2, so the two
      const y = (sum - d) / 2; // parities must match or the slot lands
      if (Number.isInteger(x) && Number.isInteger(y)) out.push({ x, y }); // between tiles (the coordinate-space trap)
    }
  }
  return out;
})();

/** The town's authored presentation surface. The broad centre stays civic
 *  stone; mirrored reflecting pools and low stone seats break the old single
 *  slab into a plaza with side courts. Obstacles are real renderer terrain,
 *  not UI decoration, and local cosmetic routing treats them as blocked. */
function buildTownSurface(): { map: number[]; terrain: number[] } {
  const map = new Array<number>(TOWN_PLANE_W * TOWN_PLANE_H).fill(1);
  const terrain = new Array<number>(TOWN_PLANE_W * TOWN_PLANE_H).fill(TERRAIN_FLOOR);
  const place = (x: number, y: number, kind: number): void => {
    map[y * TOWN_PLANE_W + x] = 0;
    terrain[y * TOWN_PLANE_W + x] = kind;
  };

  // Two five-by-four reflecting pools, softened at their outer corners and
  // mirrored across x=y. The |x-y| <= 3 centre aisle remains completely open
  // from the crowd band to the dungeon door.
  for (let x = 5; x <= 8; x++) {
    for (let y = 12; y <= 16; y++) {
      const corner = (x === 5 || x === 8) && (y === 12 || y === 16);
      if (corner) continue;
      place(x, y, TERRAIN_POND);
      place(y, x, TERRAIN_POND);
    }
  }

  // Low stone seats on the pools' outer banks. These reuse the committed
  // table terrain art: the hub gives the same substrate a civic purpose.
  for (const [x, y] of [[4, 13], [4, 15], [13, 4], [15, 4]] as const) {
    place(x, y, TERRAIN_TABLE);
  }
  return { map, terrain };
}

export const TOWN_SURFACE = buildTownSurface();

/**
 * THE DUNGEON DOOR — a monumental arched doorway in the north face of the
 *  square: a stone plinth, stone jambs and an arched head, two paneled and
 *  iron-banded slabs with gold ring handles, and a barred fanlight over them.
 *  Placeholder vocabulary (flat fills, dark outlines, the door/portal
 *  stone-wood-gold palette) at town scale: DOOR_W tiles across and nearly as
 *  many tall, so a hero standing in front of it is ~4× smaller than the
 *  opening.
 *
 *  Callers draw it as a painter-pass WorldProp, NOT in the host overlay: the
 *  overlay runs after every entity, which hid whoever walked in front of the
 *  door. Sorted by depth (x+y of DOOR_ANCHOR) with the heroes, occlusion is
 *  correct on both sides. Map space, camera already translated — never
 *  subtract camX/camY again.
 */
export function drawTownDoor(ctx: CanvasRenderingContext2D, u: number, sx0: number, sy0: number): void {
  const W = DOOR_W * TILE_W * u; // across: 4 tile-widths
  const H = W * DOOR_TALLNESS; // up: nearly as many — it is THAT door
  const cx = sx0; // the anchor is the door's screen centre (x−y = 0)
  const ground = sy0 + HALF_H * u; // the row's tile-centre line = its ground
  const half = W / 2;
  const top = ground - H;
  const jamb = 0.1 * W; // the stone frame's thickness
  const lw = Math.max(1, 0.012 * W); // one outline weight, used everywhere

  const stone = "#4a4652";
  const stoneDark = "#2a2731";
  const stoneLit = "#5d5867";
  const wood = "#6b4a2f";
  const woodDark = "#2a1c10";
  const void1 = "#0b0a10";
  const gold = "#c8a24a";

  ctx.save();

  // A gate needs a wall to be a threshold. Low civic wings turn the old
  // freestanding billboard into the north face of the square, while their
  // height leaves the arch as the one dominant silhouette.
  const wingW = W * 0.62;
  const wingH = H * 0.46;
  const wingTop = ground - wingH;
  const wingGrad = ctx.createLinearGradient(cx, wingTop, cx, ground);
  wingGrad.addColorStop(0, stoneLit);
  wingGrad.addColorStop(1, stoneDark);
  ctx.fillStyle = wingGrad;
  ctx.strokeStyle = "#1a1720";
  ctx.lineWidth = lw * 1.25;
  for (const side of [-1, 1] as const) {
    const x = side < 0 ? cx - half - wingW : cx + half;
    ctx.fillStyle = wingGrad;
    ctx.fillRect(x, wingTop, wingW, wingH);
    ctx.strokeRect(x, wingTop, wingW, wingH);
    ctx.fillStyle = stoneLit;
    ctx.fillRect(x - 3 * u, wingTop - 7 * u, wingW + 6 * u, 9 * u);
    ctx.strokeRect(x - 3 * u, wingTop - 7 * u, wingW + 6 * u, 9 * u);
    ctx.fillStyle = wingGrad;

    // Restrained masonry courses and staggered joints.
    ctx.strokeStyle = "rgba(20,18,26,0.48)";
    ctx.lineWidth = lw * 0.7;
    const courseH = wingH / 4;
    for (let courseNo = 1; courseNo < 4; courseNo++) {
      const y = wingTop + courseH * courseNo;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + wingW, y);
      ctx.stroke();
      const joint = x + wingW * (courseNo % 2 === 0 ? 0.38 : 0.64);
      ctx.beginPath();
      ctx.moveTo(joint, y - courseH);
      ctx.lineTo(joint, y);
      ctx.stroke();
    }

    // One cool banner per wing; gold is reserved for its civic mark and
    // the door hardware, so the warm hierarchy remains clear.
    const bannerX = x + wingW * 0.5;
    const bannerTop = wingTop + 14 * u;
    const bannerHalf = 18 * u;
    const bannerBottom = ground - 18 * u;
    ctx.fillStyle = "#24384c";
    ctx.beginPath();
    ctx.moveTo(bannerX - bannerHalf, bannerTop);
    ctx.lineTo(bannerX + bannerHalf, bannerTop);
    ctx.lineTo(bannerX + bannerHalf, bannerBottom - 8 * u);
    ctx.lineTo(bannerX, bannerBottom);
    ctx.lineTo(bannerX - bannerHalf, bannerBottom - 8 * u);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#151e29";
    ctx.stroke();
    ctx.fillStyle = gold;
    ctx.save();
    ctx.translate(bannerX, bannerTop + 23 * u);
    ctx.rotate(Math.PI / 4);
    ctx.fillRect(-5 * u, -5 * u, 10 * u, 10 * u);
    ctx.restore();
  }

  // Arched shape helper: a rectangle with a semicircular head, inset for
  // the frame (outer = the stonework, inner = the opening).
  const arch = (inset: number): void => {
    const h = half - inset;
    const r = h; // a full semicircular head
    const shoulder = top + inset + r;
    ctx.beginPath();
    ctx.moveTo(cx - h, ground);
    ctx.lineTo(cx - h, shoulder);
    ctx.arc(cx, shoulder, r, Math.PI, 0);
    ctx.lineTo(cx + h, ground);
    ctx.closePath();
  };

  // ---- plinth: one wide base slab on the ground line, a darker riser
  // under a lighter tread. (Three stacked steps looked like tabs sticking
  // out of the round arch — a base is a line, not a staircase.)
  const plinthW = half * 1.08;
  const plinthH = Math.max(4, H * 0.055);
  ctx.fillStyle = stoneDark;
  ctx.fillRect(cx - plinthW, ground - plinthH, plinthW * 2, plinthH);
  ctx.fillStyle = stoneLit;
  ctx.fillRect(cx - plinthW, ground - plinthH, plinthW * 2, Math.max(1.5, plinthH * 0.3));
  ctx.strokeStyle = "#1a1720";
  ctx.lineWidth = lw;
  ctx.strokeRect(cx - plinthW, ground - plinthH, plinthW * 2, plinthH);

  // ---- the stonework (the frame), then the opening cut into it.
  arch(0);
  const frameGrad = ctx.createLinearGradient(cx - half, top, cx + half, ground);
  frameGrad.addColorStop(0, stoneLit);
  frameGrad.addColorStop(1, stone);
  ctx.fillStyle = frameGrad;
  ctx.fill();
  ctx.strokeStyle = stoneDark;
  ctx.lineWidth = lw * 1.6;
  ctx.stroke();

  // Course lines on the jambs — stacked blocks, so the mass reads as
  // masonry rather than a grey shape.
  const inner = half - jamb;
  ctx.strokeStyle = "rgba(20,18,26,0.55)";
  ctx.lineWidth = lw;
  const course = H * 0.085;
  for (let y = ground - course; y > top + H * 0.32; y -= course) {
    ctx.beginPath();
    ctx.moveTo(cx - half, y);
    ctx.lineTo(cx - inner, y);
    ctx.moveTo(cx + inner, y);
    ctx.lineTo(cx + half, y);
    ctx.stroke();
  }

  // The opening: the way down, darkest at the bottom (a stair into
  // blackness) with a faint warm rim where the town light dies.
  arch(jamb);
  const openGrad = ctx.createLinearGradient(cx, top, cx, ground);
  openGrad.addColorStop(0, "#05040a");
  openGrad.addColorStop(1, void1);
  ctx.fillStyle = openGrad;
  ctx.fill();

  // ---- the doors themselves: two slabs under the springline, a barred
  // fanlight in the arch head above them.
  const springline = ground - H * 0.62; // where the arch starts
  const gap = Math.max(1.5, 0.012 * W);
  ctx.save();
  arch(jamb);
  ctx.clip();
  for (const side of [-1, 1] as const) {
    const dw = inner - gap / 2;
    const dx = side === -1 ? cx - inner : cx + gap / 2;
    ctx.fillStyle = wood;
    ctx.fillRect(dx, springline, dw, ground - springline);
    // Planks (vertical grain) + the slab outline.
    ctx.strokeStyle = "rgba(42,28,16,0.8)";
    ctx.lineWidth = lw;
    const plank = dw / 4;
    for (let p = 1; p < 4; p++) {
      ctx.beginPath();
      ctx.moveTo(dx + plank * p, springline);
      ctx.lineTo(dx + plank * p, ground);
      ctx.stroke();
    }
    ctx.strokeStyle = woodDark;
    ctx.lineWidth = lw * 1.4;
    ctx.strokeRect(dx, springline, dw, ground - springline);
    // A recessed panel, and two iron bands across the slab.
    ctx.strokeStyle = woodDark;
    ctx.lineWidth = lw;
    ctx.strokeRect(dx + dw * 0.16, springline + (ground - springline) * 0.12, dw * 0.68, (ground - springline) * 0.76);
    ctx.fillStyle = "#38313a";
    for (const fy of [0.18, 0.66]) {
      ctx.fillRect(dx, springline + (ground - springline) * fy, dw, Math.max(2, 0.022 * W));
    }
  }
  // Fanlight: the arch head above the doors, barred from the crown. A
  // second clip — the head only, down to the springline — because the arch
  // path still contains the door body below it, and without this the bars
  // streak across the wood as much as across the glass.
  ctx.save();
  ctx.beginPath();
  ctx.rect(cx - inner, top, inner * 2, springline - top);
  ctx.clip();
  ctx.fillStyle = "#07060c";
  ctx.fillRect(cx - inner, top, inner * 2, springline - top);
  ctx.strokeStyle = "#3a3440";
  ctx.lineWidth = lw * 1.5;
  const crown = top + jamb + inner; // the arch's centre
  for (const a of [-0.75, -0.37, 0, 0.37, 0.75]) {
    ctx.beginPath();
    ctx.moveTo(cx, crown);
    ctx.lineTo(cx + Math.sin(a * Math.PI * 0.5) * inner, crown - Math.cos(a * Math.PI * 0.5) * inner);
    ctx.stroke();
  }
  ctx.restore();
  ctx.restore();

  // Keystone at the crown + the opening's outline on top of everything.
  arch(jamb);
  ctx.strokeStyle = stoneDark;
  ctx.lineWidth = lw * 1.6;
  ctx.stroke();
  ctx.fillStyle = stoneLit;
  ctx.beginPath();
  ctx.moveTo(cx - W * 0.045, top + jamb - H * 0.02);
  ctx.lineTo(cx + W * 0.045, top + jamb - H * 0.02);
  ctx.lineTo(cx + W * 0.032, top + jamb + H * 0.055);
  ctx.lineTo(cx - W * 0.032, top + jamb + H * 0.055);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = stoneDark;
  ctx.lineWidth = lw;
  ctx.stroke();

  // ---- the handles: gold rings at the meeting edge, the one warm thing
  // on the whole front, and the scale reference that says "huge".
  const ring = W * 0.05;
  const ringY = springline + (ground - springline) * 0.46;
  ctx.strokeStyle = gold;
  ctx.lineWidth = Math.max(1.5, 0.014 * W);
  for (const side of [-1, 1] as const) {
    ctx.beginPath();
    ctx.arc(cx + side * (gap / 2 + ring * 1.35), ringY, ring, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.restore();
}

/** A compact iron-and-stone brazier. Static flame shapes keep the town free
 *  of a second simulation clock while still giving the square warm focus. */
export function drawBrazier(ctx: CanvasRenderingContext2D, u: number, sx0: number, sy0: number): void {
  const ground = sy0 + HALF_H * u;
  ctx.save();

  const glow = ctx.createRadialGradient(sx0, ground - 28 * u, 1, sx0, ground - 28 * u, 52 * u);
  glow.addColorStop(0, "rgba(240, 174, 67, 0.24)");
  glow.addColorStop(1, "rgba(240, 174, 67, 0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(sx0, ground - 28 * u, 52 * u, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#2a2731";
  ctx.strokeStyle = "#141219";
  ctx.lineWidth = Math.max(1, 1.5 * u);
  ctx.beginPath();
  ctx.moveTo(sx0 - 13 * u, ground - 4 * u);
  ctx.lineTo(sx0 + 13 * u, ground - 4 * u);
  ctx.lineTo(sx0 + 9 * u, ground - 32 * u);
  ctx.lineTo(sx0 - 9 * u, ground - 32 * u);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#4a4652";
  ctx.fillRect(sx0 - 17 * u, ground - 38 * u, 34 * u, 7 * u);
  ctx.strokeRect(sx0 - 17 * u, ground - 38 * u, 34 * u, 7 * u);

  ctx.fillStyle = "#d66d2c";
  ctx.beginPath();
  ctx.moveTo(sx0 - 9 * u, ground - 38 * u);
  ctx.quadraticCurveTo(sx0 - 5 * u, ground - 62 * u, sx0, ground - 52 * u);
  ctx.quadraticCurveTo(sx0 + 8 * u, ground - 70 * u, sx0 + 10 * u, ground - 38 * u);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#f0c064";
  ctx.beginPath();
  ctx.moveTo(sx0 - 3 * u, ground - 39 * u);
  ctx.quadraticCurveTo(sx0, ground - 55 * u, sx0 + 4 * u, ground - 42 * u);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/** The plaza's civic seal, inlaid in the tile at the crowd band's centre. */
export function drawPlazaSeal(ctx: CanvasRenderingContext2D, u: number, sx0: number, sy0: number): void {
  const hw = (TILE_W / 2) * u;
  const hh = HALF_H * u;
  const cy = sy0 + hh;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(sx0, sy0 + 3 * u);
  ctx.lineTo(sx0 + hw - 6 * u, cy);
  ctx.lineTo(sx0, sy0 + TILE_W * 0.5 * u - 3 * u);
  ctx.lineTo(sx0 - hw + 6 * u, cy);
  ctx.closePath();
  ctx.fillStyle = "rgba(30, 42, 55, 0.9)";
  ctx.fill();
  ctx.strokeStyle = "rgba(200, 162, 74, 0.62)";
  ctx.lineWidth = Math.max(1, 1.25 * u);
  ctx.stroke();

  const r = 10 * u;
  ctx.beginPath();
  ctx.arc(sx0, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(sx0, cy - r * 0.72);
  ctx.lineTo(sx0 + r * 0.42, cy);
  ctx.lineTo(sx0, cy + r * 0.72);
  ctx.lineTo(sx0 - r * 0.42, cy);
  ctx.closePath();
  ctx.fillStyle = "rgba(200, 162, 74, 0.34)";
  ctx.fill();
  ctx.restore();
}
